import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit2, FiEye, FiEyeOff, FiTrash2 } from 'react-icons/fi'
import { Link } from 'react-router-dom'
import Badge from '../../../../../components/ui/Badge'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectCurrentBranch } from '../../../../../store/selectors'
import { getItemDescription, getItemName, getItemPrice } from '../../../../../utils/menuItemHelper'
import { MenuItem, updateMenuItem } from '../../../../../utils/supabaseClient'
import { useBranch } from '../../../../../context/BranchContext'
import { toggleMenuItemPublishStatus } from '../../../../../store/slices/menuItemSlice'

interface MenuItemListCardProps {
	item: MenuItem,
	handleEditClick?: (menuItem: MenuItem) => void,
	handleDeleteClick?: (menuItem: MenuItem) => void,
	categoryName: string,
	onPublishToggle?: (updatedItem: MenuItem) => void,
	/** When true, renders only the <td> cells without a wrapping <tr> (for DnD rows) */
	asCells?: boolean,
}

const MenuItemListCard: React.FC<MenuItemListCardProps> = ({
	item,
	handleEditClick = () => { },
	handleDeleteClick = () => { },
	categoryName,
	onPublishToggle = () => { },
	asCells = false,
}) => {
	const { t, i18n } = useTranslation()
	const branch = useAppSelector(selectCurrentBranch)
	const dispatch = useAppDispatch()
	const { selectedCurrency } = useBranch()
	const [isTogglingPublish, setIsTogglingPublish] = useState(false)

	const currencyCode = selectedCurrency?.iso || 'USD'
	const rate = selectedCurrency?.is_base ? 1 : (selectedCurrency?.exchange_rate ?? 1)
	const baseAmount = getItemPrice(item)
	const displayAmount = baseAmount * rate
	const originalBase = item.price ?? 0
	const originalDisplay = originalBase * rate

	const handlePublishToggle = async () => {
		if (isTogglingPublish) {
			return
		}

		const previousStatus = item.is_published
		const nextStatus = !previousStatus

		setIsTogglingPublish(true)
		dispatch(toggleMenuItemPublishStatus(item.id))
		onPublishToggle({ ...item, is_published: nextStatus })

		try {
			await updateMenuItem({
				id: item.id,
				is_published: nextStatus
			})
		} catch (error) {
			console.error('Error toggling publish status:', error)
			dispatch(toggleMenuItemPublishStatus(item.id))
			onPublishToggle({ ...item, is_published: previousStatus })
		} finally {
			setIsTogglingPublish(false)
		}
	}

	const cells = (
		<>
			<td className="px-3 py-2 whitespace-nowrap">
				<div className="flex items-center">
					{item.image_url ? (
						<img
							src={item.image_url}
							alt={item.name}
							className="w-10 h-10 rounded-full object-cover"
						/>
					) : (
						branch?.default_item_image ? (
							<img
								src={branch?.default_item_image}
								alt={item.name}
								className="w-10 h-10 rounded-full object-cover"
							/>
						) : (
							<div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
								<span className="text-gray-500 dark:text-gray-400 text-xs">
									{getItemName(item, i18n.language)?.charAt(0)}
								</span>
							</div>
						)
					)}
					<div className="ms-4 min-w-0">
						<div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[180px]">
							<Link to={`/dashboard/${branch?.id}/menu/${item.id}`} className="hover:underline">
								{getItemName(item, i18n.language)}
							</Link>
						</div>
						<div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[180px]">
							{getItemDescription(item, i18n.language)}
						</div>
					</div>
				</div>
			</td>
			<td className="px-3 py-2 whitespace-nowrap">
				<div className="text-sm text-gray-500 dark:text-gray-400">
					{categoryName}
				</div>
			</td>
			<td className="px-3 py-2 whitespace-nowrap">
				<div className="text-sm text-gray-900 dark:text-white">
					{new Intl.NumberFormat(i18n.language, {
						style: 'currency',
						currency: currencyCode,
					}).format(displayAmount)}
				</div>
				{!!item.discounted_price && (
					<div className="text-xs text-red-600 line-through">
						{new Intl.NumberFormat(i18n.language, {
							style: 'currency',
							currency: currencyCode,
						}).format(originalDisplay)}
					</div>
				)}
			</td>
			<td className="px-3 py-2 whitespace-nowrap">
				<div className="flex flex-col gap-1">
					{!item.is_published && (
						<Badge variant="gray" className="inline-flex text-xs">
							{t('menuItems.draft')}
						</Badge>
					)}
					{!item.is_available && (
						<Badge variant="error" className="inline-flex text-xs">
							{t('menuItems.unavailable')}
						</Badge>
					)}
					{item.is_published && item.is_available && (
						<Badge variant="success" className="inline-flex text-xs">
							{t('menuItems.published')}
						</Badge>
					)}
					{item.is_featured && (
						<Badge variant="primary" className="inline-flex text-xs">
							{t('menuItems.featured')}
						</Badge>
					)}
				</div>
			</td>
			<td className="px-3 py-2 whitespace-nowrap text-end text-sm font-medium">
				<div className="flex justify-end gap-2">
					<button
						onClick={handlePublishToggle}
						disabled={isTogglingPublish}
						className="text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 disabled:opacity-50 cursor-pointer transition-colors"
						title={item.is_published ? t('common.unpublish') : t('common.publish')}
					>
						{isTogglingPublish ? (
							<div className="animate-spin">
								<FiEyeOff className="h-5 w-5" />
							</div>
						) : item.is_published ? (
							<FiEye className="h-5 w-5" />
						) : (
							<FiEyeOff className="h-5 w-5" />
						)}
						<span className="sr-only">{item.is_published ? t('common.unpublish') : t('common.publish')}</span>
					</button>
					<button
						onClick={() => handleEditClick(item)}
						className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer"
					>
						<FiEdit2 className="h-5 w-5" />
						<span className="sr-only">{t('common.edit')}</span>
					</button>
					<button
						onClick={() => handleDeleteClick(item)}
						className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 cursor-pointer"
					>
						<FiTrash2 className="h-5 w-5" />
						<span className="sr-only">{t('common.delete')}</span>
					</button>
				</div>
			</td>
		</>
	);

	if (asCells) return cells;

	return (
		<tr className="hover:bg-gray-600 dark:hover:bg-gray-750">
			{cells}
		</tr>
	);
}

export default MenuItemListCard